import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { get, find, filter, includes } from 'lodash'
import _ from 'lodash'

import Operations from '../../../../operations'
import { colors } from '@lmc/common/constants'
import dayjs from 'dayjs'
import {
  Table,
  Spinner,
  HeaderSection,
  NoDataFound,
} from '../../../../components/generic'
import PropertyFilters from '../../../../components/filters/components/PropertyFilters'
import { BarChart } from '../../../../components/charts'
import { getTableData, getMonths } from '../../GroupReports/utils'
import { withRouter } from '../../../../withRouter'

const DISPLAY_DATE_FORMAT = 'MMM'
const DATA_DATE_FORMAT = 'MM/YYYY'

/**
@description Converts the data in the correct format for the
chart/table

@param {array} data - An array of arrays, with each array
having the format of ['label', 1, 1 etc]. Each value in a
given array is the value for a certain month. All the arrays
are in the same month order.

@returns {array} series - An array of objects, with each
object containing three key-value pairs: the category name,
the data for that category, and the color associated with that
category.

E.g. [{name: "Extremely obese", data: Array(12), color: "#FF3366"},]

*/
const convertToSeries = (data) => {
  let series = data.map((row) => {
    const [name, ...data] = row
    const color = styles.weightColours[name]
    return { name, data, color }
  })

  return series
}

const renderHeader = (id, data, activeFilters, setActiveFilters, keys) => {
  const allFilters = {
    item: {
      label: 'BMI Category',
      filters:
        data &&
        data.map((row) => ({
          value: row.key,
          label: keys[row.key],
        })),
    },
  }

  return (
    <div>
      <HeaderSection backLink={`/homes/${id}/dashboard`} title={`BMI`} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingTop: 15,
        }}
      >
        <div style={{ flex: 1 }}>
          <PropertyFilters
            activeFilters={activeFilters}
            allFilters={allFilters}
            onChange={({ key, value }) => {
              const exists = includes(activeFilters[key], value)
              if (!value.length) {
                // This handles clearing all filters
                setActiveFilters({ item: [] })
              } else {
                setActiveFilters({
                  ...activeFilters,
                  [key]: exists
                    ? filter(activeFilters[key], (i) => i !== value)
                    : [...(activeFilters[key] || []), value],
                })
              }
            }}
            expandedView
          />
        </div>
        <div style={{ flexGrow: 1 }} />
      </div>
    </div>
  )
}

const keys = {
  healthy: 'Healthy',
  underweight: 'Underweight',
  overweight: 'Overweight',
  obese: 'Obese',
  extremelyObese: 'Extremely obese',
}

const formatData = (dataset, from, to, activeFilters) => {
  const data = dataset.map((row) => ({
    key: keys[row.key],
    data: row.data,
    filterKey: row.key,
  }))

  const months = getMonths(from, to)

  const columns = [
    {
      label: 'Month',
      sortable: false,
      width: 150,
    },
    ...data.map((entry) => ({
      label: entry.key,
      sortable: false,
      type: 'percentage',
      icon: {
        ...styles.headerIcons.roundedSquare,
        color: styles.weightColours[entry.key],
      },
    })),
  ]

  let filteredData = data.filter(
    (row) =>
      !activeFilters.item ||
      !activeFilters.item.length ||
      activeFilters.item.includes(row.filterKey)
  )

  const categories = months.map((month) => month.format(DISPLAY_DATE_FORMAT))
  const monthKeys = months.map((month) => ({
    key: month.format(DATA_DATE_FORMAT),
  }))

  const chartData = getTableData(filteredData, monthKeys)
  const tableData = getTableData(data, monthKeys)

  const chartSeries = convertToSeries(chartData)
  const tableSeries = convertToSeries(tableData)

  chartSeries.reverse()
  tableSeries.reverse()

  let tableDataByMonth = months.map((month, index) => {
    return [
      month.format('MMMM'),
      ...data.map((entry) => {
        return find(tableSeries, ['name', entry.key]).data[index]
      }),
    ]
  })

  return {
    chartSeries,
    tableDataByMonth,
    categories,
    columns,
  }
}

const renderResult = (data, from, to, activeFilters) => {
  const { chartSeries, tableDataByMonth, categories, columns } = formatData(
    data,
    from,
    to,
    activeFilters
  )

  return (
    <div style={{ paddingTop: '30px' }}>
      <BarChart
        categories={categories}
        stacked={'percent'}
        legend={{ reversed: true }}
        series={chartSeries}
        height={400}
        yAxisTickFormat={'{value}%'}
        yAxisTickInterval={25}
        symbolRadius={0}
      />
      <Table data={tableDataByMonth} columns={columns} />
    </div>
  )
}

const renderEmpty = () => {
  return (
    <div style={{ paddingTop: 5 }}>
      <NoDataFound
        heading={'Nothing to see here...'}
        text={'This site has no BMI data to display.'}
      />
    </div>
  )
}

export function HomeBMICharts(props) {
  const {
    data,
    fetchData,
    isLoading,
    router: { params },
  } = props

  useEffect(() => {
    fetchData(params.id)
  }, [params.id])

  // const [to, setToDate] = useState(dayjs())
  // const [from, setFromDate] = useState(dayjs().subtract(1, 'year').startOf('month'))
  const to = dayjs()
  const from = dayjs().subtract(1, 'year').startOf('month')
  const [activeFilters, setActiveFilters] = useState({})

  return (
    <div style={styles.reportContainer}>
      {renderHeader(params.id, data, activeFilters, setActiveFilters, keys)}
      {isLoading ? (
        <Spinner />
      ) : data && data.length ? (
        renderResult(data, from, to, activeFilters)
      ) : (
        renderEmpty()
      )}
    </div>
  )
}

const styles = {
  headerIcons: {
    roundedSquare: {
      name: 'roundedSquare',
      size: 8,
      viewBox: '0 0 8 8',
      marginTop: 8,
    },
  },
  reportContainer: {
    margin: '0 auto',
    maxWidth: 960,
    padding: '30px 0px 80px 0px',
    position: 'relative',
  },
  weightColours: {
    'Extremely obese': colors.red,
    Obese: colors.orange,
    Overweight: colors.yellow,
    Underweight: colors.blue,
    Healthy: colors.green,
  },
}

HomeBMICharts.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
}

const STATE_ID = 'group_site_bmi'
const mapStateToProps = (state) => {
  return {
    data: _.get(state, `data.requests.${STATE_ID}.response`),
    isLoading: get(state, `data.requests.${STATE_ID}.isLoading`),
    error: get(state, `data.requests.${STATE_ID}.error`),
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    fetchData: (id) => {
      dispatch(
        Operations.fetchData({
          url: 'aggregations/bmi-history',
          query: { home: id },
          id: STATE_ID,
        })
      )
    },
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(HomeBMICharts))
